import dayjs from "dayjs";
import "dayjs/locale/es-mx.js";
import "dayjs/locale/pt-br.js";
import "dayjs/locale/zh-cn.js";
import "dayjs/locale/it.js";
import "dayjs/locale/pl.js";
import {
  ListReturnsState,
  ReturnDetailsForList,
  totalPageCount,
} from "./common";
import { FilterType } from "./reducer";
import { ALL_ACCOUNTS, RETURNS_EXCEL } from "../../../../utils/constants";
import { getPendingDownloadJobs } from "../../../../utils/notificationsUtils";
import { commonDateFormat, dayJSLang } from "../../utils";
import { AllReturnsState, ReturnsRows } from "../allReturnsGrid/AllReturnsGrid";
import { FilterWithInfo, ReturnTypeEnum } from "../allReturnsGrid/types";
import { TabStatusCount } from "../allReturnsTab/AllReturnsTab";

export type StatusIconInfo = {
  type: string;
  greyedOut: boolean;
  showFullName: boolean;
};

const statusIconOrder = [
  "inProcess",
  "needAction",
  "waitingForNFe",
  "waitingOnSupplier",
  "dealerPreparation",
  "readyToShip",
  "rejected",
  "completed",
];

export const sortFieldMap: { [key: string]: string } = {
  returnId: "RETURN_ID",
  returnType: "RETURN_TYPE",
  dealerReference: "DEALER_REFERENCE",
  returnParts: "RETURNS_PARTS",
  returnValue: "RETURN_VALUE",
  returnDate: "RETURN_DATE",
  invoiceNumber: "INVOICE_NUMBER",
  nfeNumber: "NOTA_FISCAL_NUMBER",
  dealerAccount: "DEALER_ACCOUNT",
};

export const getReturnRows = (
  state: ListReturnsState,
  lang: string,
  returnTypeMap: { [key: string]: string }
): ReturnsRows[] =>
  state.isLoading
    ? []
    : state.returns.map((returnObj) => ({
        dealerAccount: returnObj.dealerId,
        returnId: returnObj.returnId,
        returnType: returnTypeMap[returnObj.returnType],
        returnTypeCode: returnObj.returnType,
        dealerReference: returnObj.dealerReference,
        invoiceNumber: returnObj.invoiceNumber ?? "-",
        nfeNumber: returnObj.nfeNumber ?? "-",
        returnParts: Number.parseInt(returnObj.lineItemsCount, 10),
        returnValue: returnObj.totalReturnAmount,
        returnDate: dayjs(returnObj.creationDate)
          .locale(dayJSLang[lang])
          .format(commonDateFormat),
        returnStatus: returnObj.returnStatusList,
        statusUpdates: returnObj.latestComment ?? "",
        hasMultipleStatus: returnObj.returnStatusList.length > 1,
        statusIconInfo: getStatusIconInfo(returnObj.returnStatusList, state),
        isCancelDisabled: isCancelDisabled(returnObj, state),
        returnDetailsUrl: getReturnDetailsUrl(state, returnObj),
        fetchedFromCache: returnObj.fetchedFromCache,
      }));

function getReturnDetailsUrl(
  state: ListReturnsState,
  returnDetails: ReturnDetailsForList
) {
  const path =
    returnDetails.returnType === ReturnTypeEnum.ZCRR
      ? `/returns/${returnDetails.returnId}`
      : `/returns/surplus/${returnDetails.returnId}`;
  const searchParams = isMultipleAccountsSelected(state)
    ? `?dealerId=${returnDetails.dealerId}`
    : "";
  return path + searchParams;
}

export const getIsInvoiceFilterDisabled = (filters: FilterWithInfo): boolean =>
  filters.returnType === ReturnTypeEnum.ZSPR;

export const getAllReturnsState = (
  state: ListReturnsState
): AllReturnsState => ({
  tabIndex: state.tabIndex,
  filters: state.filters,
  pageNumber: state.pageNumber,
  pageSize: state.pageSize,
  sortField: state.sortField,
  sortDir: state.sortDir,
});

function isCancelDisabled(
  returnDetails: ReturnDetailsForList,
  state: ListReturnsState
): boolean {
  if (!isCreateReturnsEnabled(state)) {
    return true;
  }
  return (
    returnDetails.returnType === ReturnTypeEnum.NMQ ||
    returnDetails.fetchedFromCache ||
    (!returnDetails.returnStatusList.includes("inProcess") &&
      !returnDetails.returnStatusList.includes("dealerPreparation"))
  );
}

function getStatusIconInfo(
  returnStatusList: string[],
  state: ListReturnsState
): StatusIconInfo[] {
  return [...returnStatusList]
    .sort(statusSort)
    .map((returnStatus) => ({
      type: returnStatus,
      greyedOut: shouldGreyOut(state, returnStatus),
      showFullName: returnStatusList.length === 1,
    }))
    .sort(statusIconSort);
}

export function getStatusTabs(state: ListReturnsState) {
  const showNFe = showWaitingForNFe(state);
  const showSupplier = showWaitingOnSupplier(state);
  const showDealerPrep = showDealerPreparation(state);
  const showScrapStatus = showScrapCreditPending(state);
  return [
    "allReturns",
    "inProcess",
    "needAction",
    ...(showNFe ? ["waitingForNFe"] : []),
    ...(showSupplier ? ["waitingOnSupplier"] : []),
    ...(showDealerPrep ? ["dealerPreparation"] : []),
    ...(showScrapStatus ? ["scrap"] : []),
    "readyToShip",
    "rejected",
    "completed",
  ];
}

function shouldGreyOut(state: ListReturnsState, returnStatus: string) {
  const statusTabs = getStatusTabs(state);
  return state.tabIndex === 0
    ? false
    : statusTabs[state.tabIndex] !== returnStatus;
}

function statusSort(statusA: string, statusB: string) {
  return statusIconOrder.indexOf(statusA) - statusIconOrder.indexOf(statusB);
}

function statusIconSort(
  statusInfoA: StatusIconInfo,
  statusInfoB: StatusIconInfo
) {
  return Number(statusInfoA.greyedOut) - Number(statusInfoB.greyedOut);
}

export const getStatusCount = (state: ListReturnsState): TabStatusCount => {
  const stateCount = state.statusCount;
  return {
    allReturns: stateCount.allReturns ?? 0,
    inProcess: stateCount.inProcess ?? 0,
    needAction: stateCount.needAction ?? 0,
    waitingForNFe: stateCount.waitingForNFe ?? 0,
    waitingOnSupplier: stateCount.waitingOnSupplier ?? 0,
    dealerPreparation: stateCount.dealerPreparation ?? 0,
    readyToShip: stateCount.readyToShip ?? 0,
    rejected: stateCount.rejected ?? 0,
    completed: stateCount.completed ?? 0,
    scrap: stateCount.scrap ?? 0,
  };
};

export const getTabIndex = (state: ListReturnsState): number => state.tabIndex;

export const getCurrency = (state: ListReturnsState): string =>
  state.returns.length > 0 ? `(${state.returns[0].currency})` : "";

export function isAuthenticationError(state: ListReturnsState): boolean {
  return (
    state.error?.response?.data?.statusCode === 401 ||
    state.error?.response?.status === 401 ||
    state.error?.response?.data?.statusCode === 403 ||
    state.error?.response?.status === 403
  );
}

export function isTechnicalError(state: ListReturnsState): boolean {
  return state.error !== undefined && !isAuthenticationError(state);
}

export function showDefaultAllReturns(state: ListReturnsState): boolean {
  return (
    !hasError(state) && !isLoading(state) && returnsDataNotAvailable(state)
  );
}

export function isLoading(state: ListReturnsState): boolean {
  return state.isLoading && !state.isReturnsLoaded;
}

export function isTableLoading(state: ListReturnsState): boolean {
  return state.isLoading && state.isReturnsLoaded;
}

export function showAllReturnsGrid(state: ListReturnsState): boolean {
  const showGridDuringInitialLoading =
    !!state.featureToggles.isLcpOptimisationReturnsListEnabled &&
    isLoading(state);

  return (
    !hasError(state) &&
    (showGridDuringInitialLoading || returnsDataAvailable(state))
  );
}

function hasError(state: ListReturnsState): boolean {
  return isAuthenticationError(state) || isTechnicalError(state);
}

export function getStatusForFilter(state: ListReturnsState): string {
  const statusTabs = getStatusTabs(state);
  return statusTabs[state.tabIndex];
}

export function showAllReturnsHeader(state: ListReturnsState): boolean {
  const showHeaderDuringInitialLoading =
    !!state.featureToggles.isLcpOptimisationReturnsListEnabled &&
    isLoading(state);

  return (
    !hasError(state) &&
    (showHeaderDuringInitialLoading || returnsDataAvailable(state))
  );
}

export function showAllReturnsTab(state: ListReturnsState): boolean {
  const showTabsDuringInitialLoading =
    !!state.featureToggles.isLcpOptimisationReturnsListEnabled &&
    isLoading(state);

  return (
    !hasError(state) &&
    (showTabsDuringInitialLoading || returnsDataAvailable(state))
  );
}

export function getShowCancelModal(state: ListReturnsState): boolean {
  return state.showCancelModal;
}

export function getReturnAttributesToCancel(state: ListReturnsState): {
  returnId: string;
  returnTypeCode: string;
  returnStatusList: string[];
} {
  return state.returnAttributesToCancel;
}

export function hasDealerPreparationStatus(state: ListReturnsState): boolean {
  return state.returnAttributesToCancel.returnStatusList.includes(
    "dealerPreparation"
  );
}

function returnsDataAvailable(state: ListReturnsState): boolean {
  return (
    state.isReturnsLoaded &&
    (state.isFilterApplied ||
      (state.statusCount.allReturns !== undefined &&
        state.statusCount.allReturns > 0))
  );
}

function returnsDataNotAvailable(state: ListReturnsState): boolean {
  return (
    state.isReturnsLoaded &&
    state.statusCount.allReturns !== undefined &&
    !state.isFilterApplied &&
    state.statusCount.allReturns === 0
  );
}

export function getFilters(state: ListReturnsState): FilterWithInfo {
  return state.filters;
}

export function isUserAuthorizedToEditReturn(state: ListReturnsState): boolean {
  return state.isUserAuthorizedToEditReturn;
}

export function isJdpsDailyReturnActivitiesEnabled(
  state: ListReturnsState
): boolean {
  return state.featureToggles.isJdpsDailyReturnActivitiesEnabled ?? false;
}

export function showReturnValueColumn(state: ListReturnsState): boolean {
  return (
    !state.featureToggles.isJdpsDailyReturnActivitiesEnabled &&
    !state.featureToggles.isJdpsSurplusReturnActivitiesEnabled
  );
}

export function isJdpsDealer(state: ListReturnsState): boolean {
  return state.featureToggles.isJdpsDealer ?? false;
}

export function getDownloadReturnsInProgressStatus(
  state: ListReturnsState
): boolean {
  const accountKeys = [ALL_ACCOUNTS, ...getAccountIds(state)];
  return accountKeys.some((key) =>
    getPendingDownloadJobs(key).some((job) => job.type === RETURNS_EXCEL)
  );
}

export function hideAllReturnsFilters(state: ListReturnsState): boolean {
  return hasError(state) || !returnsDataAvailable(state);
}

export function getCurrentPage(state: ListReturnsState): number {
  const pageCount = totalPageCount(state);
  return pageCount >= 1 ? state.pageNumber + 1 : 0;
}

export function getRowCount(state: ListReturnsState): number {
  const statusCount = state.statusCount as {
    [key: string]: number;
  };
  const statusTabs = getStatusTabs(state);
  return statusCount[statusTabs[state.tabIndex]] ?? 0;
}

export function getSortFieldName(state: ListReturnsState): string | undefined {
  return sortFieldMap[state.sortField]
    ? sortFieldMap[state.sortField]
    : undefined;
}

const BRAZIL = "BR";

export function showNFENumber(state: ListReturnsState): boolean {
  return state.dealerLocation === BRAZIL;
}

function isCreateReturnsEnabledInJDPO(state: ListReturnsState): boolean {
  return state.showCreateReturns;
}

function isCreateReturnsEnabled(state: ListReturnsState) {
  return (
    (state.featureToggles.isRBACEnabled &&
      state.featureToggles.isCreateReturnEnabled &&
      state.isUserAuthorizedToCreateReturn) ||
    (!state.featureToggles.isRBACEnabled && isCreateReturnsEnabledInJDPO(state))
  );
}

function isSurplusReturnsEnabled(state: ListReturnsState) {
  return (
    state.featureToggles.isSurplusReturnsEnabled &&
    (!isJdpsDealer(state) || isJdpsSurplusReturnActivitiesEnabled(state))
  );
}

function isCreateDailyReturnEnabled(state: ListReturnsState): boolean {
  if (isJdpsDealer(state)) {
    return state.featureToggles.isJdpsDailyReturnActivitiesEnabled ?? false;
  }
  return isCreateReturnsEnabled(state);
}

export function showDefaultMessage(state: ListReturnsState): boolean {
  return !isCreateReturnsEnabled(state) && !isSurplusReturnsEnabled(state);
}

export function showCreateDailyReturns(state: ListReturnsState): boolean {
  return isCreateDailyReturnEnabled(state) && !isSurplusReturnsEnabled(state);
}

export function getCreateReturnOptions(
  state: ListReturnsState,
  dailyOption: string,
  surplusOption: string
): { displayName: string; value: string }[] {
  return [
    {
      displayName: isCreateDailyReturnEnabled(state) ? dailyOption : "",
      value: "daily",
    },
    {
      displayName: isSurplusReturnsEnabled(state) ? surplusOption : "",
      value: "surplus",
    },
  ].filter((option) => option.displayName !== "");
}

function isJdpsSurplusReturnActivitiesEnabled(
  state: ListReturnsState
): boolean {
  return state.featureToggles.isJdpsSurplusReturnActivitiesEnabled ?? false;
}

export function showSurplusReturn(state: ListReturnsState): boolean {
  return state.isUserAuthorizedToCreateReturn && isSurplusReturnsEnabled(state);
}

export function isMultipleAccountsSelected(state: ListReturnsState): boolean {
  return (
    state.isUserAuthorizedForMultipleAccounts &&
    state.selectedAccounts.length > 1
  );
}

export function getAccountId(state: ListReturnsState): string {
  return state.accountId;
}

export function getAccountIds(state: ListReturnsState): string[] {
  return isMultipleAccountsSelected(state)
    ? state.selectedAccounts.map((acc) => acc.id)
    : [state.accountId];
}

export function isDebouncedSearch(searchField: FilterType): boolean {
  return [
    FilterType.PartNumber,
    FilterType.ReturnId,
    FilterType.DealerReference,
    FilterType.InvoiceNumber,
    FilterType.NotaFiscalNumber,
    FilterType.DealerAccount,
  ].includes(searchField);
}

export function showWaitingForNFe(state: ListReturnsState): boolean {
  return (
    state.featureToggles.isSurplusReturnsEnabled &&
    state.dealerLocation === BRAZIL &&
    (state.filters.returnType === ReturnTypeEnum.ALL ||
      state.filters.returnType === ReturnTypeEnum.ZSPR ||
      state.filters.returnType === ReturnTypeEnum.NMQ)
  );
}

export function showWaitingOnSupplier(state: ListReturnsState) {
  return state.featureToggles.isVendorDirectReturnEnabled;
}

export function showDealerPreparation(state: ListReturnsState) {
  return state.featureToggles.isDealerPreparationEnabled;
}

export function showScrapCreditPending(state: ListReturnsState) {
  return (
    showDealerPreparation(state) &&
    (state.featureToggles.isDailyScrapCreditPendingEnabled ?? true)
  );
}

export function showCachedSurplusReturns(state: ListReturnsState): boolean {
  const showInProcessData = state.tabIndex === 0 || state.tabIndex === 1;
  const returnTypeFilterIncludesSurplusReturn =
    state.filters.returnType === ReturnTypeEnum.ALL ||
    state.filters.returnType === ReturnTypeEnum.ZSPR;

  return (
    isSurplusReturnsEnabled(state) &&
    returnTypeFilterIncludesSurplusReturn &&
    state.pageNumber === 0 &&
    showInProcessData
  );
}

export function showPaginationFooter(state: ListReturnsState): boolean {
  return !isTableLoading(state) && getRowCount(state) > 10;
}
